import React, { useCallback, useMemo } from 'react';
import _ from 'underscore';

import { STEP_MANAGER_ACTIONS } from './step-manager-actions';

export function useStepListRenderer({
  config, dispatch, state, shouldSave, shouldScrollToMe, scrollToExpandedStep,
}) {
  const { expandedStepIndex, shouldSaveList, statusList } = state;

  const firstErrorIndex = useMemo(() =>
    _.findIndex(statusList, status => !_.isEmpty(status.errorString)), [statusList]);

  const expand = useCallback(index => () => dispatch({
    type: STEP_MANAGER_ACTIONS.EXPAND,
    toExpandedStepIndex: index,
  }), [dispatch]);

  const collapse = useCallback(() => {
    dispatch({
      type: STEP_MANAGER_ACTIONS.COLLAPSE,
    });
  }, [dispatch]);

  const onExpandClicked = useCallback(index => () => {
    collapse();
    expand(index)();
  }, [collapse, expand]);

  const onGoToNextClicked = useCallback(index => () => {
    collapse();
    expand(index + 1)();
  }, [collapse, expand]);

  const updateStatus = useCallback(index => (status) => {
    dispatch({
      type: STEP_MANAGER_ACTIONS.UPDATE_STATUS,
      index,
      status,
    });
  }, [dispatch]);

  const updateData = useCallback(index => (data) => {
    dispatch({
      type: STEP_MANAGER_ACTIONS.UPDATE_DATA,
      index,
      data,
    });
  }, [dispatch]);

  const steps = useMemo(() => {
    const stepList = _.map(config, (s, index) => {
      const isExpanded = expandedStepIndex === index;
      const shouldScrollToStep = (isExpanded && scrollToExpandedStep) ||
                      (shouldScrollToMe && firstErrorIndex === index);
      const onToggle = isExpanded ? collapse : onExpandClicked(index);
      const onGoToNext = onGoToNextClicked(index);
      const onStatusUpdate = updateStatus(index);
      const onDataChange = updateData(index);
      const stepShouldSave = (shouldSave || shouldSaveList[index])
        && statusList[index].hasUnsavedChange && _.isEmpty(statusList[index].errorString);

      return (
        <s.component
          key={s.key}
          onToggle={onToggle}
          onGoToNext={onGoToNext}
          isExpanded={isExpanded}
          onStatusUpdate={onStatusUpdate}
          onDataChange={onDataChange}
          shouldSave={stepShouldSave}
          shouldScrollToMe={shouldScrollToStep}
          errorMessage={statusList[index].errorString}
          {...s.props}
        />
      );
    });
    return stepList;
  }, [collapse, config, expandedStepIndex, firstErrorIndex, onExpandClicked, onGoToNextClicked,
    shouldSave, shouldSaveList, shouldScrollToMe, statusList, updateData, updateStatus, scrollToExpandedStep]);

  return steps;
}
